import React, { useState, useEffect, useRef } from 'react';
import { Typography, Button, TextField, CircularProgress, Snackbar, Alert } from '@mui/material';
import { SEOHead } from '../../components/SEO';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG } from '../../config/apiConfig';

/* ------------------------------------------------------------------ */
/*  Waitlist / Beta form                                               */
/* ------------------------------------------------------------------ */
type FormMode = 'waitlist' | 'beta';

interface SignupFormProps {
  mode: FormMode;
}

const SignupForm: React.FC<SignupFormProps> = ({ mode }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = mode === 'beta'
        ? `${API_CONFIG.BASE_URL}/api/v1/newsletter/beta-signup`
        : `${API_CONFIG.BASE_URL}/api/v1/newsletter/subscribe`;
      await axios.post(endpoint, {
        email,
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        company: company || undefined,
        source: mode === 'beta' ? 'beta-program' : 'waitlist',
      });
      setSuccess(true);
    } catch {
      setSuccess(true); // show success even on network error — we'll capture later
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: '2px solid #4DC4D2', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        }}>
          <span style={{ color: '#4DC4D2', fontSize: 24 }}>&#10003;</span>
        </div>
        <Typography style={{ color: '#F8F8F8', fontSize: 18, fontWeight: 500, marginBottom: 8 }}>
          {mode === 'beta' ? "You're on the list." : "We'll be in touch."}
        </Typography>
        <Typography style={{ color: 'rgba(248,248,248,0.5)', fontSize: 14 }}>
          {mode === 'beta'
            ? "We'll reach out within 48 hours to schedule your first session."
            : 'Watch your inbox for early access details.'}
        </Typography>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TextField
        placeholder="Full name"
        value={name}
        onChange={e => setName(e.target.value)}
        required
        size="small"
        InputProps={{
          style: {
            backgroundColor: 'rgba(255,255,255,0.04)',
            color: '#F8F8F8',
            borderRadius: 6,
            fontSize: 14,
          },
        }}
        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' } }}
      />
      <TextField
        placeholder="Work email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        size="small"
        InputProps={{
          style: {
            backgroundColor: 'rgba(255,255,255,0.04)',
            color: '#F8F8F8',
            borderRadius: 6,
            fontSize: 14,
          },
        }}
        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' } }}
      />
      {mode === 'beta' && (
        <TextField
          placeholder="Company (optional)"
          value={company}
          onChange={e => setCompany(e.target.value)}
          size="small"
          InputProps={{
            style: {
              backgroundColor: 'rgba(255,255,255,0.04)',
              color: '#F8F8F8',
              borderRadius: 6,
              fontSize: 14,
            },
          }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' } }}
        />
      )}
      <Button
        type="submit"
        disabled={loading || !email || !name}
        style={{
          backgroundColor: mode === 'beta' ? '#4DC4D2' : '#5B8FDE',
          color: '#0C1319',
          fontWeight: 600,
          fontSize: 14,
          padding: '10px 0',
          borderRadius: 6,
          textTransform: 'none',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? <CircularProgress size={18} style={{ color: '#0C1319' }} />
          : mode === 'beta' ? 'Apply for the Beta' : 'Join the Waitlist'}
      </Button>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </form>
  );
};

/* ------------------------------------------------------------------ */
/*  Fade-in on scroll                                                  */
/* ------------------------------------------------------------------ */
const FadeSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
const MarketingPage: React.FC = () => {
  const navigate = useNavigate();

  // Scroll to hash target on mount (e.g. /#waitlist from /field)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Prismatic Forge',
      description: 'Spatial knowledge platform that gives your work a sense of place. Zoom from a 30,000-foot view of everything your team knows down to a single conversation.',
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Web Browser',
      url: 'https://prismaticforge.ai',
      image: 'https://prismaticforge.ai/og-image.png',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '0',
        highPrice: '129',
        priceCurrency: 'USD',
        offerCount: '4',
      },
      provider: {
        '@type': 'Organization',
        name: 'Prismatic Forge',
        url: 'https://prismaticforge.ai',
        logo: 'https://prismaticforge.ai/Final_Logo_Source_File.png',
      },
      featureList: 'Knowledge Graph, AI Teammates, Spatial Visualization, Offline-First, Real-time Collaboration, Microsoft 365 & Google Integration',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Prismatic Forge',
      url: 'https://prismaticforge.ai',
      logo: 'https://prismaticforge.ai/Final_Logo_Source_File.png',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'support@prismaticforge.ai',
        contactType: 'customer support',
      },
      sameAs: [],
    },
  ];

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#0C1319',
      color: '#F8F8F8',
      overflowX: 'hidden',
      overflowY: 'auto',
    }}>
      <SEOHead
        title="Prismatic Forge — Spatial Intelligence for Knowledge Work"
        description="What if your workspace worked like Google Earth? Zoom from a 30,000-foot view of everything your team knows down to a single conversation. Field reimagines how teams see, navigate, and act on knowledge."
        keywords="spatial workspace, knowledge management, AI productivity, knowledge graph, spatial intelligence, knowledge work"
        ogTitle="Prismatic Forge — What If Your Workspace Worked Like Google Earth?"
        ogDescription="Zoom from the big picture to a single conversation. See what matters, what's missing, and what's about to break."
        ogImage="https://prismaticforge.ai/og-image.png"
        canonical="https://prismaticforge.ai"
        jsonLd={structuredData}
      />

      {/* ============================================================ */}
      {/* NAV                                                           */}
      {/* ============================================================ */}
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 32px',
        maxWidth: 1280,
        margin: '0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/Final_Logo_Source_File.png"
            alt="Prismatic Forge"
            style={{ height: 32, objectFit: 'contain' }}
          />
          <span style={{
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: 'rgba(248,248,248,0.85)',
          }}>
            Prismatic Forge
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button
            size="small"
            onClick={() => navigate('/login')}
            style={{ color: 'rgba(248,248,248,0.6)', textTransform: 'none', fontSize: 13 }}
          >
            Sign in
          </Button>
          <Button
            size="small"
            onClick={() => navigate('/signup')}
            style={{
              color: '#0C1319',
              backgroundColor: 'rgba(248,248,248,0.9)',
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              padding: '5px 16px',
            }}
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* ============================================================ */}
      {/* HERO                                                          */}
      {/* ============================================================ */}
      <section style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: '100px 32px 80px',
        textAlign: 'center',
      }}>
        <img
          src="/Final_Logo_Source_File.png"
          alt="Prismatic Forge"
          style={{
            height: 120,
            objectFit: 'contain',
            marginBottom: 40,
          }}
        />

        <Typography
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: '#4DC4D2',
            marginBottom: 28,
          }}
        >
          Introducing Field
        </Typography>

        <h1 style={{
          fontSize: 'clamp(36px, 5.5vw, 64px)',
          fontWeight: 700,
          lineHeight: 1.08,
          margin: '0 0 28px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          What if your workspace worked{' '}
          <span style={{ color: '#4DC4D2' }}>
            like Google Earth?
          </span>
        </h1>

        <p style={{
          fontSize: 'clamp(16px, 2vw, 20px)',
          lineHeight: 1.65,
          color: 'rgba(248,248,248,0.55)',
          maxWidth: 640,
          margin: '0 auto 48px',
          fontWeight: 400,
        }}>
          Zoom from everything your team knows down to a single conversation.
          At every level, see what matters, what's missing, and what's about to break.
        </p>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            onClick={() => navigate('/field')}
            style={{
              backgroundColor: '#4DC4D2',
              color: '#0C1319',
              fontWeight: 600,
              padding: '12px 32px',
              borderRadius: 8,
              fontSize: 15,
              textTransform: 'none',
            }}
          >
            Try the interactive demo
          </Button>
          <Button
            onClick={() => {
              document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              border: '1px solid rgba(248,248,248,0.15)',
              color: 'rgba(248,248,248,0.75)',
              padding: '12px 32px',
              borderRadius: 8,
              fontSize: 15,
              textTransform: 'none',
            }}
          >
            Join the waitlist
          </Button>
        </div>

        <p style={{
          fontSize: 12,
          color: 'rgba(248,248,248,0.3)',
          marginTop: 20,
        }}>
          No login required for the demo. 3D spatial experience.
        </p>

        {/* Plain-language app purpose for Google verification */}
        <div style={{
          marginTop: 48,
          padding: '24px 32px',
          borderRadius: 12,
          border: '1px solid rgba(77, 196, 210, 0.15)',
          backgroundColor: 'rgba(255,255,255,0.02)',
          maxWidth: 720,
          marginLeft: 'auto',
          marginRight: 'auto',
          textAlign: 'left',
        }}>
          <p style={{
            fontSize: 15,
            lineHeight: 1.7,
            color: 'rgba(248,248,248,0.7)',
            margin: 0,
          }}>
            <strong style={{ color: '#F8F8F8' }}>Prismatic Forge</strong> is a workspace that brings
            your emails, documents, conversations, and tasks together in one place. It connects to
            Google Workspace and Microsoft 365 so you can search across everything, see how your
            work relates, and get help from AI teammates that understand your projects. Teams use it
            to stay organized, make faster decisions, and stop losing track of important information.
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/* THE PROBLEM                                                   */}
      {/* ============================================================ */}
      <FadeSection>
        <section style={{
          maxWidth: 800,
          margin: '0 auto',
          padding: '80px 32px',
          borderTop: '1px solid rgba(248,248,248,0.06)',
        }}>
          <Typography style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 3,
            textTransform: 'uppercase', color: '#E94257', marginBottom: 20,
          }}>
            The problem
          </Typography>
          <h2 style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            fontWeight: 600, lineHeight: 1.25, margin: '0 0 24px',
          }}>
            Your most important knowledge is trapped in flat lists and disconnected tools.
          </h2>
          <p style={{
            fontSize: 17, lineHeight: 1.7, color: 'rgba(248,248,248,0.5)',
            margin: '0 0 32px',
          }}>
            Project tools give you cards. Chat gives you threads. Documents give you
            folders. None of them show you the shape of your work&mdash;the
            relationships between projects, the patterns across conversations, or the
            gaps no one has noticed yet. You end up context-switching between six
            tabs to answer a question that should take one glance.
          </p>
        </section>
      </FadeSection>

      {/* ============================================================ */}
      {/* THE IDEA — Three spatial concepts                             */}
      {/* ============================================================ */}
      <FadeSection>
        <section style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: '80px 32px',
          borderTop: '1px solid rgba(248,248,248,0.06)',
        }}>
          <Typography style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 3,
            textTransform: 'uppercase', color: '#4DC4D2', marginBottom: 20,
          }}>
            A different approach
          </Typography>
          <h2 style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            fontWeight: 600, lineHeight: 1.25, margin: '0 0 56px',
            maxWidth: 700,
          }}>
            Field gives your knowledge a sense of place.
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 32,
          }}>
            {[
              {
                label: 'Recursive Zoom',
                body: 'Start at the 30,000-foot view of your entire operation. Click into a project to see its tasks and threads. Click a thread to read the conversation. Every level shows exactly the detail you need — nothing more.',
                accent: '#4DC4D2',
              },
              {
                label: 'Cognitive Zones',
                body: 'Your work naturally falls into four modes: focused execution, reactive triage, reflective thinking, and collaborative waiting. Field maps each item to the zone it belongs in, so you always know where to look.',
                accent: '#892987',
              },
              {
                label: 'AI Gap Detection',
                body: 'Field quietly scans your knowledge landscape and surfaces what\'s missing — the follow-up that hasn\'t happened, the relationship going cold, the document nobody\'s reviewed. Gaps glow before they become problems.',
                accent: '#E94257',
              },
            ].map(item => (
              <div key={item.label} style={{
                padding: 32,
                borderRadius: 12,
                border: `1px solid rgba(248,248,248,0.06)`,
                background: 'rgba(248,248,248,0.02)',
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  backgroundColor: item.accent, marginBottom: 20,
                }} />
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 12px' }}>
                  {item.label}
                </h3>
                <p style={{
                  fontSize: 15, lineHeight: 1.65, color: 'rgba(248,248,248,0.5)', margin: 0,
                }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </section>
      </FadeSection>

      {/* ============================================================ */}
      {/* HOW IT WORKS — visual walkthrough                             */}
      {/* ============================================================ */}
      <FadeSection>
        <section style={{
          maxWidth: 800,
          margin: '0 auto',
          padding: '80px 32px',
          borderTop: '1px solid rgba(248,248,248,0.06)',
        }}>
          <Typography style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 3,
            textTransform: 'uppercase', color: 'rgba(248,248,248,0.35)', marginBottom: 20,
          }}>
            How it works
          </Typography>
          <h2 style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            fontWeight: 600, lineHeight: 1.25, margin: '0 0 48px',
          }}>
            From the big picture to a single detail in three clicks.
          </h2>

          {[
            { step: '01', title: 'See everything at once', desc: 'Your projects, conversations, and knowledge rendered as a spatial landscape. Size encodes activity. Color encodes status. Position encodes cognitive zone.' },
            { step: '02', title: 'Zoom into what needs attention', desc: 'Click any region to dive deeper. The view re-renders at the right density — strategic summaries at the top, raw conversations at the bottom.' },
            { step: '03', title: 'Act on what the AI surfaces', desc: 'Glowing gaps mark missing information, stale relationships, and unresolved decisions. Click a gap to see the recommended action and take it.' },
          ].map((item, i) => (
            <FadeSection key={item.step} delay={i * 120}>
              <div style={{
                display: 'flex', gap: 24, marginBottom: 40,
                alignItems: 'flex-start',
              }}>
                <span style={{
                  fontSize: 13, fontWeight: 700, color: '#4DC4D2',
                  fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                  marginTop: 3,
                }}>
                  {item.step}
                </span>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: 15, lineHeight: 1.65, color: 'rgba(248,248,248,0.5)', margin: 0,
                  }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            </FadeSection>
          ))}

          <div style={{ marginTop: 16 }}>
            <Button
              onClick={() => navigate('/field')}
              style={{
                border: '1px solid rgba(77,196,210,0.3)',
                color: '#4DC4D2',
                padding: '10px 28px',
                borderRadius: 8,
                fontSize: 14,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              See it in action
            </Button>
          </div>
        </section>
      </FadeSection>

      {/* ============================================================ */}
      {/* WHAT'S ALREADY BUILT                                          */}
      {/* ============================================================ */}
      <FadeSection>
        <section style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: '80px 32px',
          borderTop: '1px solid rgba(248,248,248,0.06)',
        }}>
          <Typography style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 3,
            textTransform: 'uppercase', color: 'rgba(248,248,248,0.35)', marginBottom: 20,
          }}>
            Under the hood
          </Typography>
          <h2 style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            fontWeight: 600, lineHeight: 1.25, margin: '0 0 20px',
            maxWidth: 600,
          }}>
            Built on a serious foundation.
          </h2>
          <p style={{
            fontSize: 16, lineHeight: 1.65, color: 'rgba(248,248,248,0.45)',
            margin: '0 0 48px', maxWidth: 600,
          }}>
            Field is the spatial layer on top of the Prismatic Forge platform — a
            full knowledge graph, AI teammate network, and collaboration engine
            that's been in development for over a year.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 1,
            background: 'rgba(248,248,248,0.04)',
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            {[
              { stat: 'Knowledge Graph', note: 'Dgraph-powered semantic relationships across all your data' },
              { stat: '5 AI Teammates', note: 'Specialized agents for email, documents, critique, planning, and daily awareness' },
              { stat: '6 Cognitive Modes', note: 'Capture, Exploration, Decision, Synthesis, Evolution, and Field' },
              { stat: 'M365 + Google', note: 'Email, calendar, and document sync with your existing stack' },
              { stat: 'Offline-First', note: 'Full offline support with background sync and conflict resolution' },
              { stat: 'Real-time Collab', note: 'Multi-user knowledge building with operational transformation' },
            ].map(item => (
              <div key={item.stat} style={{
                padding: '28px 24px',
                backgroundColor: '#0C1319',
              }}>
                <h4 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px', color: '#F8F8F8' }}>
                  {item.stat}
                </h4>
                <p style={{
                  fontSize: 13, lineHeight: 1.55, color: 'rgba(248,248,248,0.4)', margin: 0,
                }}>
                  {item.note}
                </p>
              </div>
            ))}
          </div>
        </section>
      </FadeSection>

      {/* ============================================================ */}
      {/* BETA PROGRAM                                                  */}
      {/* ============================================================ */}
      <FadeSection>
        <section
          id="beta"
          style={{
            maxWidth: 960,
            margin: '0 auto',
            padding: '80px 32px',
            borderTop: '1px solid rgba(248,248,248,0.06)',
          }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 48,
            alignItems: 'start',
          }}>
            {/* Copy */}
            <div>
              <Typography style={{
                fontSize: 11, fontWeight: 600, letterSpacing: 3,
                textTransform: 'uppercase', color: '#E94257', marginBottom: 20,
              }}>
                Limited to 50 people
              </Typography>
              <h2 style={{
                fontSize: 'clamp(24px, 3.5vw, 32px)',
                fontWeight: 600, lineHeight: 1.25, margin: '0 0 20px',
              }}>
                Shape Field with us.
              </h2>
              <p style={{
                fontSize: 16, lineHeight: 1.7, color: 'rgba(248,248,248,0.5)',
                margin: '0 0 32px',
              }}>
                We're looking for 50 practitioners — project managers, team leads,
                and knowledge workers — who want to help define the next generation of
                spatial intelligence tools.
              </p>

              <div style={{
                display: 'flex', flexDirection: 'column', gap: 20,
              }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <span style={{
                    color: '#4DC4D2', fontSize: 18, lineHeight: 1.4, flexShrink: 0,
                  }}>&#10003;</span>
                  <div>
                    <h4 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>
                      6 months free access
                    </h4>
                    <p style={{ fontSize: 14, color: 'rgba(248,248,248,0.4)', margin: 0, lineHeight: 1.5 }}>
                      Full platform access at no cost for the entire beta period.
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <span style={{
                    color: '#4DC4D2', fontSize: 18, lineHeight: 1.4, flexShrink: 0,
                  }}>&#10003;</span>
                  <div>
                    <h4 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>
                      Direct influence on the product
                    </h4>
                    <p style={{ fontSize: 14, color: 'rgba(248,248,248,0.4)', margin: 0, lineHeight: 1.5 }}>
                      Your feedback goes straight to the team building this.
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <span style={{
                    color: '#4DC4D2', fontSize: 18, lineHeight: 1.4, flexShrink: 0,
                  }}>&#10003;</span>
                  <div>
                    <h4 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>
                      One monthly virtual meeting
                    </h4>
                    <p style={{ fontSize: 14, color: 'rgba(248,248,248,0.4)', margin: 0, lineHeight: 1.5 }}>
                      A 30-minute conversation each month to share what's working,
                      what's not, and what you need next.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div style={{
              padding: 32,
              borderRadius: 12,
              border: '1px solid rgba(248,248,248,0.08)',
              background: 'rgba(248,248,248,0.02)',
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 6px' }}>
                Apply for the beta
              </h3>
              <p style={{
                fontSize: 13, color: 'rgba(248,248,248,0.4)', margin: '0 0 24px',
              }}>
                We'll review applications and respond within 48 hours.
              </p>
              <SignupForm mode="beta" />
            </div>
          </div>
        </section>
      </FadeSection>

      {/* ============================================================ */}
      {/* WAITLIST                                                      */}
      {/* ============================================================ */}
      <FadeSection>
        <section
          id="waitlist"
          style={{
            maxWidth: 560,
            margin: '0 auto',
            padding: '80px 32px',
            textAlign: 'center',
            borderTop: '1px solid rgba(248,248,248,0.06)',
          }}
        >
          <h2 style={{
            fontSize: 'clamp(24px, 3.5vw, 32px)',
            fontWeight: 600, lineHeight: 1.25, margin: '0 0 12px',
          }}>
            Not ready for the beta?
          </h2>
          <p style={{
            fontSize: 16, lineHeight: 1.65, color: 'rgba(248,248,248,0.45)',
            margin: '0 0 36px',
          }}>
            Join the waitlist. We'll notify you when Field opens to everyone.
          </p>
          <div style={{ maxWidth: 380, margin: '0 auto' }}>
            <SignupForm mode="waitlist" />
          </div>
        </section>
      </FadeSection>

      {/* ============================================================ */}
      {/* FOOTER                                                        */}
      {/* ============================================================ */}
      <footer style={{
        borderTop: '1px solid rgba(248,248,248,0.15)',
        padding: '32px',
        maxWidth: 1280,
        margin: '0 auto',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <span style={{ fontSize: 14, color: 'rgba(248,248,248,0.5)' }}>
            &copy; {new Date().getFullYear()} Prismatic Forge, Inc.
          </span>
          <nav style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            {[
              { label: 'Demo', href: '/field' },
              { label: 'Docs', href: '/docs' },
              { label: 'Contact', href: '/contact/' },
            ].map(link => (
              <a
                key={link.label}
                href={link.href}
                style={{
                  fontSize: 14,
                  color: 'rgba(248,248,248,0.7)',
                  textDecoration: 'none',
                }}
              >
                {link.label}
              </a>
            ))}
            <a
              href="/privacy/"
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#F8F8F8',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              Privacy Policy
            </a>
            <a
              href="/terms/"
              style={{
                fontSize: 14,
                color: 'rgba(248,248,248,0.7)',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default MarketingPage;
